import type { ClientInstanceOf } from '@elara/client-core/firebase/types';
import type { TripModel } from '@elara/client-core/models/TripModel';

import React, { useState } from 'react';

import { useToastState, useTripsState } from '#states/hooks.js';
import { Button, Calendar, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '#ui/index.js';

interface EditTripDatesDialogProps {
  trip: ClientInstanceOf<TripModel>;
}

/**
 * Dialog for editing trip dates
 */
export function EditTripDatesDialog({ trip }: EditTripDatesDialogProps): React.ReactNode {
  const tripsState = useTripsState();
  const toastState = useToastState();
  const [isSaving, setIsSaving] = useState(false);

  async function handleSave() {
    if (!trip.id || !tripsState.isDateFormValid || isSaving) return;

    const tripId = trip.id;
    const startDate = tripsState.newStartDate;
    // If only start date is selected, use it for both start and end
    const endDate = tripsState.newEndDate || tripsState.newStartDate;

    setIsSaving(true);
    try {
      await tripsState.updateTrip(tripId, { startDate, endDate });
      tripsState.closeDateDialog();
    } catch (error) {
      console.error('Failed to update trip dates:', error);
      toastState.addToast({
        title: 'Failed to update dates',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleClear() {
    if (!trip.id) return;

    try {
      await tripsState.updateTrip(trip.id, { startDate: null, endDate: null });
      tripsState.closeDateDialog();
    } catch (error) {
      console.error('Failed to clear trip dates:', error);
      toastState.addToast({ title: 'Failed to clear dates', description: 'Please try again.', variant: 'destructive' });
    }
  }

  function handleCancel() {
    if (isSaving) return;
    tripsState.closeDateDialog();
  }

  function handleDayClick(clickedDate: Date) {
    const hasCompleteRange = tripsState.newStartDate && tripsState.newEndDate;
    const hasStartOnly = tripsState.newStartDate && !tripsState.newEndDate;

    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const dayStr = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${dayStr}`;
    };

    if (hasCompleteRange) {
      // If we have a complete range, clicking any date starts a new range
      tripsState.setNewStartDate(formatDate(clickedDate));
      tripsState.setNewEndDate('');
    } else if (hasStartOnly) {
      // If we only have a start date, clicking sets the end date
      const startDate = new Date(tripsState.newStartDate);

      // If clicked date is before start date, swap them
      if (clickedDate < startDate) {
        tripsState.setNewEndDate(tripsState.newStartDate);
        tripsState.setNewStartDate(formatDate(clickedDate));
      } else {
        // Otherwise, set it as the end date
        tripsState.setNewEndDate(formatDate(clickedDate));
      }
    } else {
      // No selection yet, set as start date
      tripsState.setNewStartDate(formatDate(clickedDate));
      tripsState.setNewEndDate('');
    }
  }

  if (!tripsState.isDateDialogOpen) return undefined;

  return (
    <Dialog
      key={`${trip.id}-date-dialog`}
      open={tripsState.isDateDialogOpen}
      onOpenChange={(open) => !open && !isSaving && handleCancel()}
    >
      <DialogContent className="sm:max-w-full sm:w-auto">
        <DialogHeader>
          <DialogTitle>When are you going?</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center">
          <Calendar
            mode="range"
            defaultMonth={
              tripsState.newStartDate
                ? new Date(tripsState.newStartDate)
                : tripsState.newEndDate
                  ? new Date(tripsState.newEndDate)
                  : new Date()
            }
            selected={
              tripsState.newStartDate && tripsState.newEndDate
                ? { from: new Date(tripsState.newStartDate), to: new Date(tripsState.newEndDate) }
                : tripsState.newStartDate
                  ? { from: new Date(tripsState.newStartDate) }
                  : undefined
            }
            onDayClick={handleDayClick}
            onSelect={() => {
              // Prevent default onSelect behavior - we handle everything in onDayClick
              // This prevents react-day-picker from interfering with our custom logic
            }}
            numberOfMonths={tripsState.isMobile ? 1 : 2}
            disabled={(day: Date) => day < new Date(Date.now() - 86400000)}
            className="mt-2"
          />
        </div>
        <DialogFooter className="mt-0 sm:justify-between">
          <Button variant="ghost" onClick={handleClear} disabled={isSaving}>
            Clear dates
          </Button>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
              Close
            </Button>
            <Button onClick={handleSave} disabled={!tripsState.isDateFormValid || isSaving} isBusy={isSaving}>
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
